import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * KRF 2026-10-05: lines are changed by line item key, not position. free-gift-picker.js adds
 * and removes the gift line in the background, which renumbers every other line, so a
 * position read from the drawer could change or remove the wrong line. Captured when the
 * shopper acts, because a later morph can rewrite the same <tr> to show another line.
 *
 * @typedef {object} LineRef
 * @property {string} key - The line item key.
 * @property {string} variantId - The row's variant id, used to recover from a stale key.
 * @property {boolean} subscription - Whether the row is on a selling plan.
 * @property {number} sameKindRendered - Rows on screen with the same variant and subscription state, this one included.
 * @property {string[]} renderedKeys - Every line key on screen when the shopper acted.
 */

/** KRF 2026-10-05: shown after a failed change, once the cart is re-rendered from the server. */
const ERROR_MESSAGE = 'Could not update your cart. Please try again.';
const STALE_LINE_MESSAGE = 'Your cart just changed. Please check it and try again.';

/**
 * KRF 2026-10-05: shopper cart changes in flight, mirrored on <html> as data-krf-cart-busy so
 * free-gift-picker.js can wait. .cart-items-disabled alone is not enough: any section morph
 * strips it, and bar-promo-pricing.js swaps in a new cart-items-component without it.
 */
let changesInFlight = 0;

/**
 * KRF 2026-10-05: shopper cart changes started, mirrored on <html> as data-krf-cart-seq. A
 * background re-render requested before a change started is older than that change's own
 * response, so it is dropped instead of morphed over it (it brought back removed rows).
 */
let changesStarted = 0;

/** @param {number} delta */
function trackCartChange(delta) {
  if (delta > 0) {
    changesStarted += 1;
    document.documentElement.setAttribute('data-krf-cart-seq', String(changesStarted));
  }

  changesInFlight = Math.max(0, changesInFlight + delta);
  document.documentElement.toggleAttribute('data-krf-cart-busy', changesInFlight > 0);
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  // KRF 2026-10-05: an arrow, not .bind(this), which drops the .cancel() that #removeLine uses
  #debouncedOnChange = debounce((event, line) => this.#onQuantityChange(event, line), 300);

  /**
   * KRF 2026-10-05: the line the pending debounced quantity change is for.
   * @type {string | undefined}
   */
  #pendingChangeKey;

  /** KRF 2026-10-05: background re-renders requested, so only the latest one is morphed. */
  #backgroundRenders = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
  }

  /**
   * KRF 2026-10-05: reads the line from the row the event came from while it is being
   * dispatched, then hands it to the debounced handler. Product page selectors have no row.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantitySelectorUpdate = (event) => {
    const row = event.target instanceof Element ? event.target.closest('.cart-items__table-row[data-key]') : null;

    if (!(row instanceof HTMLElement) || !row.dataset.key || !this.contains(row)) return;

    this.#pendingChangeKey = row.dataset.key;
    this.#debouncedOnChange(event, this.#describeLine(row.dataset.key));
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   * @param {LineRef} line - The line, captured when the event was dispatched.
   */
  #onQuantityChange(event, line) {
    const { quantity } = event.detail;

    this.#pendingChangeKey = undefined;

    if (!line) return;

    if (quantity === 0) {
      return this.#removeLine(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#rowForKey(line.key);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * KRF 2026-10-05: cart-products.liquid passes the line item key, not the line position.
   * @param {string} key - The line item key.
   */
  onLineItemRemove(key) {
    if (typeof key !== 'string' || !key) return;

    this.#removeLine(this.#describeLine(key));
  }

  /**
   * Removes a line and animates its row out.
   * @param {LineRef} line - The line.
   */
  #removeLine(line) {
    // KRF 2026-10-05: a quantity change for this line still waiting on the debounce would reach
    // Shopify after the removal, hit a key that is gone and show a false "cart just changed".
    // Only for this line: the debounce is one timer, and another line's change must still go.
    if (this.#pendingChangeKey === line.key) {
      this.#debouncedOnChange.cancel();
      this.#pendingChangeKey = undefined;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    // The row to remove and all of its nested lines
    const rowsToRemove = this.#rows().filter(
      (row) => row.dataset.key === line.key || row.dataset.parentKey === line.key
    );

    // Add class to the row to trigger the animation. KRF 2026-10-05: rows are hidden, not
    // deleted, until the server answers. If the change fails, #handleCartError shows the row
    // again even when the re-render fails too (offline); on success the response morph removes it.
    rowsToRemove.forEach((row) => {
      if (prefersReducedMotion()) {
        row.classList.add('removing');
        return this.#hideRow(row);
      }

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Hide the row after the animation ends. KRF 2026-10-05: only while it still has
      // .removing. onAnimationEnd also fires when a re-render cancels the animation, and by then
      // the morph may have reused this <tr> for another line or restored it after an error.
      onAnimationEnd(row, () => {
        if (row.classList.contains('removing')) this.#hideRow(row);
      });
    });
  }

  /**
   * KRF 2026-10-05: hides a removed row until the server answers. Its quantity input is left out
   * of the cart form meanwhile, as a deleted row's would be: Checkout posts updates[] by position.
   * @param {HTMLElement} row - The row.
   */
  #hideRow(row) {
    row.style.setProperty('display', 'none');
    row.querySelectorAll('input[name="updates[]"]:not([disabled])').forEach((input) => {
      input.setAttribute('disabled', '');
      input.setAttribute('data-krf-row-hidden', '');
    });
  }

  /**
   * Updates the quantity.
   * KRF 2026-10-05: posts the line item key (id), not the position (line). A stale key is
   * retried once when it can be matched to exactly one line; any other failure re-renders the
   * cart from the server and shows a message. Returns the whole round trip, retry included.
   * @param {Object} config - The config.
   * @param {LineRef} config.line - The line, captured when the shopper acted.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {boolean} [config.retried] - Whether this is the retry after a stale key.
   * @returns {Promise<void>}
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();
    trackCartChange(1);

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: line.key,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    let status = 0;

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        status = response.status;
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          // KRF 2026-10-05: 400 is a stale key ("no valid id"): the line was re-keyed or removed
          // since the cart was rendered. Its error text is not for shoppers.
          if (status === 400) {
            return config.retried
              ? this.#handleCartError(line.key, STALE_LINE_MESSAGE)
              : this.#recoverStaleLine(config);
          }

          const { errors } = parsedResponseText;
          return this.#handleCartError(line.key, typeof errors === 'string' ? errors : ERROR_MESSAGE);
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        // KRF 2026-10-05: bar-promo-pricing.js may have replaced this element while the change was
        // in flight, and an event from a detached element never reaches document
        (this.isConnected ? this : document).dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        // KRF 2026-10-05: a network failure used to leave a removed row gone and say nothing
        resetShimmer(this);
        return this.#handleCartError(line.key, ERROR_MESSAGE);
      })
      .finally(() => {
        this.#enableCartItems();
        trackCartChange(-1);
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * KRF 2026-10-05: recovers from a stale line item key. Retries once only when the tapped row
   * was the only one on screen with its variant and subscription state, and exactly one line
   * like it is in the cart now, under a key that was not on screen when the shopper acted: the
   * same line under a new key, for example after bar-promo-pricing.js moved it to another
   * selling plan. With two such lines, "mine was re-keyed" and "mine is gone and the other was
   * re-keyed" look the same. Anything less certain re-renders the cart and shows a message, so
   * a line the shopper did not pick is never changed.
   * @param {Object} config - The updateQuantity config that failed.
   * @param {LineRef} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @returns {Promise<void>}
   */
  async #recoverStaleLine(config) {
    const { line, quantity } = config;

    /** @type {{ items?: Array<{ key: string, variant_id: number, selling_plan_allocation?: object }> }} */
    let cart;
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      cart = await response.json();
    } catch (error) {
      console.error(error);
      return this.#handleCartError(line.key, ERROR_MESSAGE);
    }

    const sameItem = (cart.items ?? []).filter(
      (item) =>
        String(item.variant_id) === line.variantId && Boolean(item.selling_plan_allocation) === line.subscription
    );
    const [candidate] = sameItem;

    const onlyOfItsKind = line.sameKindRendered === 1 && sameItem.length === 1;

    if (onlyOfItsKind && candidate && !line.renderedKeys.includes(candidate.key)) {
      return this.updateQuantity({ ...config, line: { ...line, key: candidate.key }, retried: true });
    }

    // Removing a line that is no longer in the cart: it is already gone, so just show the cart
    if (quantity === 0 && line.variantId && sameItem.length === 0) {
      return this.#handleCartError(line.key);
    }

    return this.#handleCartError(line.key, STALE_LINE_MESSAGE);
  }

  /**
   * KRF 2026-10-05: re-renders the cart and shows a message on a line, for cart changes made
   * outside this component (the delivery frequency dropdown in cart-products.liquid).
   * @param {string} key - The line item key.
   * @param {string} [message] - The message to show.
   * @returns {Promise<void>}
   */
  showLineError(key, message = ERROR_MESSAGE) {
    return this.#handleCartError(key, message);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * KRF 2026-10-05: never throws. Re-renders the cart from the server first, so a row that
   * already animated out comes back, then shows the message in that line's row, found by key,
   * or above the lines when the row is gone. The stock version looked the row up by position
   * and threw "Quantity input not found", so the shopper saw nothing.
   * @param {string} key - The line item key.
   * @param {string} [message] - The message to show. Without one the cart is only re-rendered.
   * @returns {Promise<void>}
   */
  #handleCartError = async (key, message) => {
    try {
      // Bring back rows hidden or animating out before re-rendering: offline the re-render fails
      // too, and the row would vanish with the message in it. Matched on .removing, not the key:
      // after a stale key retry the row still carries the old key. Not without a message: that
      // line is already gone.
      if (message) {
        this.#rows()
          .filter((row) => row.classList.contains('removing'))
          .forEach((row) => {
            row.classList.remove('removing');
            row.style.removeProperty('--row-height');
            row.style.removeProperty('display');
            row.querySelectorAll('input[data-krf-row-hidden]').forEach((input) => {
              input.removeAttribute('disabled');
              input.removeAttribute('data-krf-row-hidden');
            });
          });
      }

      const quantityInput = this.#rowForKey(key)?.querySelector('input[data-cart-quantity]');
      if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;

      try {
        await this.#renderFromServer();
        this.#updateCartQuantitySelectorButtonStates();
      } catch (error) {
        console.error(error);
      }

      if (!message) return;

      // bar-promo-pricing.js may have swapped in a new element since the change started
      const live =
        Array.from(document.querySelectorAll('cart-items-component')).find(
          (element) => element instanceof HTMLElement && element.dataset.sectionId === this.sectionId
        ) ?? this;
      const row = this.#rowForKey(key, live);
      const cartItemError = row?.querySelector('.cart-item__error-text');
      const cartItemErrorContainer = row?.querySelector('.cart-items__error');

      if (cartItemError && cartItemErrorContainer) {
        cartItemError.textContent = message;
        cartItemErrorContainer.classList.remove('hidden');
        return;
      }

      const wrapper = live.querySelector('.cart-items__wrapper');
      if (!wrapper) return;

      wrapper.querySelector(':scope > .cart-items__notice')?.remove();

      const notice = document.createElement('p');
      notice.className = 'cart-item__error cart-items__notice cart-primary-typography';
      notice.setAttribute('role', 'alert');
      notice.textContent = message;
      wrapper.prepend(notice);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * KRF 2026-10-05: re-renders this section from a fresh request. Not
   * sectionRenderer.renderSection: it hands back any request for the section already pending,
   * made before the cart changed, and on the storefront defaults to the HTML cached at load.
   * @param {Object} [options] - The options.
   * @param {boolean} [options.background] - A re-render for a cart change made elsewhere. Dropped
   * when a newer one was requested, or when a shopper change started or is in flight: that
   * change's own response is newer and has the last word.
   */
  async #renderFromServer({ background = false } = {}) {
    const render = background ? ++this.#backgroundRenders : 0;
    const started = changesStarted;
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.sectionId);

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Section ${this.sectionId} returned ${response.status}`);

    const html = await response.text();
    if (background && (render !== this.#backgroundRenders || started !== changesStarted || changesInFlight > 0)) return;

    await morphSection(this.sectionId, html);
  }

  /**
   * KRF 2026-10-05: the line the shopper is acting on, captured as they act.
   * @param {string} key - The line item key.
   * @returns {LineRef} The line.
   */
  #describeLine(key) {
    const rows = this.#rows();
    const row = rows.find((element) => element.dataset.key === key);
    /** @param {Element | undefined} element */
    const variantOf = (element) => element?.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ?? '';
    // Only lines on a selling plan render the frequency dropdown or tag
    /** @param {Element | undefined} element */
    const onPlan = (element) => Boolean(element?.querySelector('.cd-selling-plan-select, .cd-subscription-tag'));
    const variantId = variantOf(row);
    const subscription = onPlan(row);
    const sameKind = rows.filter((element) => variantOf(element) === variantId && onPlan(element) === subscription);

    return {
      key,
      variantId,
      subscription,
      sameKindRendered: sameKind.length,
      renderedKeys: rows.map((element) => element.dataset.key ?? ''),
    };
  }

  /**
   * KRF 2026-10-05: the cart rows in the DOM right now. Scoped to rows: other elements on the
   * page carry data-key too.
   * @param {Element} [root] - The cart items component to look in.
   * @returns {HTMLElement[]} The rows.
   */
  #rows(root = this) {
    return /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('.cart-items__table-row[data-key]')));
  }

  /**
   * KRF 2026-10-05: the row for a line item key.
   * @param {string} key - The line item key.
   * @param {Element} [root] - The cart items component to look in.
   * @returns {HTMLElement | undefined} The row.
   */
  #rowForKey(key, root = this) {
    return this.#rows(root).find((row) => row.dataset.key === key);
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      // KRF 2026-10-05: not sectionRenderer.renderSection, which morphs whenever its response
      // lands, even after a shopper change that started later has morphed in newer HTML. After
      // a free gift change that brought back a row the shopper had just removed.
      this.#renderFromServer({ background: true }).catch((error) => console.error(error));
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
